import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { isMobile } from 'react-device-detect';

import { Space, Button, Layout, Menu, Badge, notification, Flex, Tooltip } from 'antd';
import { UserOutlined, BellOutlined, InfoCircleFilled, InfoCircleOutlined } from '@ant-design/icons';

import Garland from '@controls/easter-eggs/garland/garland';
import ExchangeRateInfo from '@components/exchange-rate-info/exchange-rate-info';
import PushPersonalModal from '@components/notifications/push-personal-modal';

import { isNewYearAroundUs } from '@extensions/utils';

import { useAppSelector, useAppDispatch } from '@store/hooks';
import { setCurrentMenuItem, cleanupStates } from '@store/actions';
import { serverFetch } from '@flc/server';

import { IUserSession } from '@entities/user-session';

import { ImportTruckIcon, MainLogoIcon, MiniLogoIcon } from '@icons/index';

import '@core/index.css';

interface IMainLayout {
    appVersion: string | undefined;
}

const MainLayout = (props: IMainLayout) => {
    const { appVersion } = props;
    const [api, contextHolder] = notification.useNotification();

    const navigate = useNavigate();

    const { Header, Content, Sider } = Layout;

    const location = useLocation();

    const d = useAppDispatch();
    const currentMenuItem = useAppSelector<string>((s) => s.currentMenuItem);
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [siderCollapsed, setSiderCollapsed] = useState<boolean>(isMobile);

    function getItem(label: any, key: string, urlTo?: string, icon?: any, children?: Array<any>) {
        let itemlabel = urlTo ? <Link to={urlTo}>{label}</Link> : label;

        return {
            key,
            icon,
            children,
            label: itemlabel,
        };
    }

    useEffect(() => {
        const items = location.pathname.replace('/flc', '').split('/');

        let key = items[1];
        d(setCurrentMenuItem(key));
    }, []);

    const onLogout = () => {
        serverFetch(`auth/logout`, { method: 'POST' })
            .then(() => {
                d(cleanupStates());
            })
            .catch(() => {
                d(cleanupStates());
            });
    };

    const onMenuClick = (e: any) => {
        d(setCurrentMenuItem(e.key));
    };

    const items: Array<any> = [];

    items.push(getItem('Транспорт', 'import', '/flc/import', <ImportTruckIcon />));
    items.push(getItem('Профиль', 'useraccount', '/flc/useraccount', <UserOutlined />));
    items.push(getItem('Информация', 'info', '/flc/info', <InfoCircleFilled />));

    const getLogo = () => {
        return siderCollapsed ? (
            <div className='logo'>
                <MiniLogoIcon style={{ fontSize: 30, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        ) : (
            <div className='logo'>
                <div className='sub-logo'>
                    <div style={{ marginTop: 5 }}>Перевозчик</div>
                </div>
                <MainLogoIcon style={{ fontSize: 38, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        );
    };

    return (
        <>
            <Layout style={{ height: '100vh', minHeight: '100vh' }}>
                <Sider
                    collapsible={!isMobile}
                    width={250}
                    collapsed={siderCollapsed}
                    onCollapse={(e) => setSiderCollapsed(e)}
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        zIndex: 100,
                    }}
                >
                    {getLogo()}
                    <Flex style={{ height: 'calc(100vh - 120px)' }} vertical justify='space-between'>
                        <Menu selectedKeys={[currentMenuItem]} mode='inline' items={items} onClick={onMenuClick} />
                        <div className='app-version'>Версия {appVersion}</div>
                    </Flex>
                </Sider>
                <Layout>
                    <Header className='main-header'>
                        <Flex align='center' justify='flex-end' gap='small'>
                            {isNewYearAroundUs() && <Garland />}
                            <Flex align='center' justify='flex-end' gap='small' style={{ background: '#ffffff', position: 'relative' }}>
                                <Space className='user-account-info'>
                                    <div className='user'>{userSession.fullName}</div>
                                </Space>
                                <Button variant='link' color='blue' className='logout' onClick={() => onLogout()}>
                                    Выход
                                </Button>
                            </Flex>
                        </Flex>
                    </Header>
                    <Content className='main-content'>
                        <Outlet />
                    </Content>
                </Layout>
            </Layout>

            {contextHolder}
        </>
    );
};

export default MainLayout;
